import React, { useState, useEffect } from 'react';
import {
  Sparkles, Clock, MessageCircle, FileText, AlertCircle, CheckCircle, 
  TrendingUp, X, ChevronRight, Loader2, RefreshCw, Target, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../utils/auth';
import { aiApi, tasksApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AINextSteps = ({ caseId = null, compact = false, onNavigate }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('all'); // all, high, medium, low
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSuggestions();
    fetchStats();
  }, [caseId]);

  const fetchSuggestions = async () => {
    try {
      const token = auth.getToken();
      const endpoint = caseId 
        ? `${API}/ai-next-steps/suggestions/${caseId}`
        : `${API}/ai-next-steps/suggestions`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/ai-next-steps/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const generateSuggestions = async () => {
    setGenerating(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(`${API}/ai-next-steps/generate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${response.data.count} nye anbefalinger generert!`);
      await fetchSuggestions();
      await fetchStats();
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast.error('Kunne ikke generere anbefalinger');
    } finally {
      setGenerating(false);
    }
  };

  const updateSuggestion = async (suggestionId, status) => {
    try {
      const token = auth.getToken();
      await axios.patch(
        `${API}/ai-next-steps/suggestions/${suggestionId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setSuggestions(suggestions.filter(s => s.id !== suggestionId));
      await fetchStats();
      
      const message = status === 'COMPLETED' ? 'Markert som fullført!' : 'Avvist';
      toast.success(message);
    } catch (error) {
      console.error('Error updating suggestion:', error);
      toast.error('Kunne ikke oppdatere anbefaling');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'DEADLINE': return { icon: Clock, color: 'text-red-600', bg: 'bg-red-50' };
      case 'FOLLOW_UP': return { icon: MessageCircle, color: 'text-purple-600', bg: 'bg-purple-50' };
      case 'DOCUMENTATION': return { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50' };
      case 'PHASE_TRANSITION': return { icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'TIME_ENTRY': return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' };
      case 'TASK': return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' };
      case 'MESSAGE': return { icon: MessageCircle, color: 'text-purple-600', bg: 'bg-purple-50' };
      case 'RISK': return { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' };
      case 'OPPORTUNITY': return { icon: Target, color: 'text-emerald-600', bg: 'bg-emerald-50' };
      default: return { icon: Sparkles, color: 'text-gray-600', bg: 'bg-gray-50' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'border-red-300 bg-red-50/50';
      case 'MEDIUM': return 'border-amber-300 bg-amber-50/50';
      case 'LOW': return 'border-green-300 bg-green-50/50';
      default: return 'border-gray-200 bg-white/50';
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      HIGH: 'bg-red-500 text-white',
      MEDIUM: 'bg-amber-500 text-white',
      LOW: 'bg-green-500 text-white'
    };
    
    const labels = {
      HIGH: 'Høy',
      MEDIUM: 'Middels',
      LOW: 'Lav'
    };
    
    return (
      <span className={`px-2 py-0.5 text-xs font-semibold rounded-lg ${colors[priority]}`}>
        {labels[priority]}
      </span>
    );
  };

  const filteredSuggestions = filter === 'all' 
    ? suggestions 
    : suggestions.filter(s => s.priority.toLowerCase() === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Compact view for dashboard
  if (compact) {
    // Show top 3 suggestions, prioritizing HIGH but showing MEDIUM/LOW if no HIGH exists
    const topSuggestions = suggestions
      .sort((a, b) => {
        const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      })
      .slice(0, 3);
    
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">AI Anbefalinger</h3>
              <p className="text-xs text-gray-600">
                {stats?.pending || suggestions.length} ventende
                {stats?.high_priority > 0 && ` • ${stats.high_priority} høy prioritet`}
              </p>
            </div>
          </div>
          
          <button
            onClick={generateSuggestions}
            disabled={generating}
            className="px-3 py-1.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Oppdater
          </button>
        </div>

        {topSuggestions.length === 0 ? (
          <div className="text-center py-8">
            <Zap className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Alt under kontroll! 🎉</p>
            <p className="text-xs text-gray-500 mt-1">Ingen kritiske handlinger akkurat nå</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topSuggestions.map((suggestion) => {
              const { icon: Icon, color, bg } = getCategoryIcon(suggestion.category);
              
              const borderColor = suggestion.priority === 'HIGH' ? 'border-red-200 bg-red-50/30' :
                                 suggestion.priority === 'MEDIUM' ? 'border-amber-200 bg-amber-50/30' :
                                 'border-green-200 bg-green-50/30';
              
              return (
                <div
                  key={suggestion.id}
                  className={`border rounded-lg p-3 hover:shadow-md transition cursor-pointer ${borderColor}`}
                  onClick={() => {
                    if (suggestion.action_url && onNavigate) {
                      onNavigate(suggestion.action_url);
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${color}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900">
                          {suggestion.title}
                        </p>
                        {getPriorityBadge(suggestion.priority)}
                      </div>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {suggestion.description}
                      </p>
                      {suggestion.case_title && (
                        <p className="text-xs text-gray-500 mt-1">
                          Sak: {suggestion.case_title}
                        </p>
                      )}
                    </div>
                    
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {topSuggestions.length > 0 && (
          <button
            onClick={() => onNavigate && onNavigate('/lexy/ai-next-steps')}
            className="w-full mt-4 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition"
          >
            Se alle {suggestions.length} anbefalinger →
          </button>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">AI Anbefalinger</h2>
              <p className="text-sm text-gray-600">Proaktiv hjelp til å holde sakene i gang</p>
            </div>
          </div>
          
          <button
            onClick={generateSuggestions}
            disabled={generating}
            className="px-4 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Generer nye
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-xs text-gray-600">Ventende</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{stats.high_priority}</p>
              <p className="text-xs text-gray-600">Høy prioritet</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              <p className="text-xs text-gray-600">Fullført</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">{stats.dismissed}</p>
              <p className="text-xs text-gray-600">Avvist</p>
            </div>
          </div>
        )}
      </div>

      {/* Filter Buttons */}
      <div className="flex items-center gap-2">
        {['all', 'high', 'medium', 'low'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
              filter === f
                ? 'bg-gray-900 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f === 'all' ? 'Alle' : f === 'high' ? 'Høy' : f === 'medium' ? 'Middels' : 'Lav'}
          </button>
        ))}
      </div>

      {/* Suggestions List */}
      {filteredSuggestions.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">
            {filter === 'all' ? 'Ingen anbefalinger' : `Ingen ${filter} prioritet anbefalinger`}
          </p>
          <p className="text-sm text-gray-500 mt-2">Alt ser bra ut! 🎉</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSuggestions.map((suggestion) => {
            const { icon: Icon, color, bg } = getCategoryIcon(suggestion.category);
            
            return (
              <div
                key={suggestion.id}
                className={`backdrop-blur-xl backdrop-saturate-150 border-2 shadow-lg rounded-xl p-5 ${getPriorityColor(suggestion.priority)}`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-6 h-6 ${color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {suggestion.title}
                        </h3>
                        {getPriorityBadge(suggestion.priority)}
                      </div>
                    </div>

                    {suggestion.case_title && (
                      <p className="text-sm text-gray-600 mb-2">
                        📁 {suggestion.case_title}
                      </p>
                    )}

                    <p className="text-sm text-gray-700 mb-3">
                      {suggestion.description}
                    </p>

                    {suggestion.ai_reasoning && (
                      <div className="bg-white/60 border border-gray-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          💡 AI Begrunnelse:
                        </p>
                        <p className="text-xs text-gray-600">
                          {suggestion.ai_reasoning}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {suggestion.action_url && (
                        <button
                          onClick={() => onNavigate && onNavigate(suggestion.action_url)}
                          className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 flex items-center gap-2"
                        >
                          {suggestion.action_text}
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                      
                      <button
                        onClick={() => updateSuggestion(suggestion.id, 'COMPLETED')}
                        className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Fullført
                      </button>
                      
                      <button
                        onClick={() => updateSuggestion(suggestion.id, 'DISMISSED')}
                        className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 flex items-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Avvis
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AINextSteps;
